import type { ThemeMode } from "./tokens";

export const THEME_STORAGE_PREFIX = "smartefb.theme";

type UserIdentitySource = Record<string, unknown> | null | undefined;

const normalizeIdentityPart = (value: unknown): string | null => {
  if (typeof value !== "string" && typeof value !== "number") {
    return null;
  }

  const normalized = `${value}`.trim().toLowerCase();
  return normalized.length ? encodeURIComponent(normalized) : null;
};

export const getThemeIdentity = (user: UserIdentitySource): string => {
  if (!user || typeof user !== "object") {
    return "anonymous";
  }

  const candidates = [
    user.id,
    user.user_id,
    user.email,
    user.tlc,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeIdentityPart(candidate);
    if (normalized) {
      return normalized;
    }
  }

  return "anonymous";
};

export const getThemeStorageKey = (identity: string): string =>
  `${THEME_STORAGE_PREFIX}.${identity || "anonymous"}`;

export const isThemeMode = (value: unknown): value is ThemeMode =>
  value === "dark" || value === "light";

export const resolveInitialThemeMode = ({
  storedMode,
  prefersLight,
}: {
  storedMode: unknown;
  prefersLight: boolean;
}): ThemeMode => {
  if (isThemeMode(storedMode)) {
    return storedMode;
  }

  return prefersLight ? "light" : "dark";
};

const getStoredUser = (): UserIdentitySource => {
  if (typeof window === "undefined" || !window.localStorage) {
    return null;
  }

  try {
    const rawUser = window.localStorage.getItem("user");
    return rawUser ? JSON.parse(rawUser) : null;
  } catch (error) {
    return null;
  }
};

const prefersLightTheme = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-color-scheme: light)").matches;

export const readThemeMode = (
  identity = getThemeIdentity(getStoredUser()),
): ThemeMode => {
  let storedMode: string | null = null;

  try {
    storedMode =
      typeof window !== "undefined" && window.localStorage
        ? window.localStorage.getItem(getThemeStorageKey(identity))
        : null;
  } catch (error) {
    storedMode = null;
  }

  return resolveInitialThemeMode({
    storedMode,
    prefersLight: prefersLightTheme(),
  });
};

export const applyThemeMode = (mode: ThemeMode): void => {
  if (typeof document === "undefined") {
    return;
  }

  document.body.classList.toggle("light", mode === "light");
  document.body.dataset.theme = mode;
  document.documentElement.style.colorScheme = mode;
};

export const persistThemeMode = (
  identity: string,
  mode: ThemeMode,
): void => {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(getThemeStorageKey(identity), mode);
    }
  } catch (error) {
    // Theme persistence is best-effort; the selected mode still applies.
  }
};

export const bootstrapThemeMode = (): ThemeMode => {
  const mode = readThemeMode();
  applyThemeMode(mode);
  return mode;
};
